import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { publicBookingMessage, requestMyBookingReschedule } from "./api";

/**
 * Nút + form "Yêu cầu đổi lịch" của MỘT lịch khách tự đặt online.
 *
 * Tách riêng vì cùng một lịch nay hiện ở hai chỗ khác nhau tuỳ trạng thái:
 * lịch đã thành đơn thì nút nằm trên thẻ ĐƠN HÀNG (một buổi chụp một thẻ,
 * CEO chốt 23/08/2026), lịch chưa thành đơn thì nằm trên thẻ lịch. Viết hai
 * bản là sớm muộn hai chỗ lệch nhau.
 *
 * Khách tới quầy (walk-in) không có lịch online nên không có nút này — đúng
 * thật, đổi lịch của họ là gọi studio.
 */
export function RescheduleRequestForm({ bookingId }: { bookingId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [shootDate, setShootDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [note, setNote] = useState("");

  const requestM = useMutation({
    mutationFn: () => requestMyBookingReschedule({ id: bookingId, shootDate, startTime, note }),
    onSuccess: async () => {
      toast.success("Đã gửi yêu cầu đổi lịch cho CSKH");
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["customer-bookings", "me"] });
    },
    onError: (error) => toast.error(publicBookingMessage(error)),
  });

  return (
    <>
      <Button variant="outline" className="min-h-11" onClick={() => setOpen((value) => !value)}>
        <RefreshCcw className="mr-2 h-4 w-4" />
        Yêu cầu đổi lịch
      </Button>

      {open && (
        <form
          className="mt-1 grid w-full gap-3 border-t pt-4 sm:grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1fr_2fr_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            requestM.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label>Ngày mới</Label>
            <Input type="date" value={shootDate} onChange={(event) => setShootDate(event.target.value)} className="min-h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>Giờ mới</Label>
            <Input value={startTime} onChange={(event) => setStartTime(event.target.value)} placeholder="10:00" className="min-h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>Ghi chú</Label>
            <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Lý do hoặc khung giờ mong muốn" className="min-h-11" />
          </div>
          <div className="flex items-end">
            <Button className="min-h-11 w-full" disabled={!shootDate || !startTime || requestM.isPending}>
              Gửi
            </Button>
          </div>
        </form>
      )}
    </>
  );
}

/** Lịch online đã "chốt" mới cho đổi — chờ thanh toán/đã huỷ thì không. */
export function canRequestReschedule(status: string): boolean {
  return status === "confirmed" || status === "converted";
}
